import React, {useContext, useEffect, useMemo, useState} from "react";
import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import OrderDashboardFilters from "../dashboard/OrderDashboard/Layout/OrderDashboardFilters.jsx";
import {FilterContext} from "../../components/filters/DashboardFilterContextProvider.jsx";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import {ErpCalculationApi} from "../../api/authority/generated/index";
import {useErrorTranslation} from "../../api/utilities/translateErrorMessage.js";
import toast from "react-hot-toast";
import EChart from "./ReportingEChart.jsx";
import {useRenderCurrencyValue, useRenderNumber, useRenderPercentValue} from "../../api/utilities/useValueRenderers.js";
import {useTranslation} from "react-i18next";
import {
    ChannelMixColors,
    dividePercent,
    ExpandablePanelTitle,
    formatAxisTooltip,
    formatChartCurrency,
    formatChartPercent,
    formatChartNumber,
    formatCurrencyItemTooltip,
    KpiCard,
    moneyAmount,
    moneyToMajor,
    Panel,
    projectCurrency,
    ReportingPageDescription,
    SortableHeader,
    TablePagination,
    TableSearch,
} from "./ReportingLab.jsx";

function formatProductAxisLabel(value) {
    const label = String(value ?? "");
    const maxLength = 24;

    return label.length > maxLength ? `${label.slice(0, maxLength)}...` : label;
}

export default function ReportingProductProfitabilityLab({project}) {
    const {t} = useTranslation();
    const {since, until, centresAllowListed, domainsAllowListed} = useContext(FilterContext);
    const {getAuthorityConfiguration} = useContext(GlobalContext);
    const {et} = useErrorTranslation();
    const renderCurrencyValue = useRenderCurrencyValue();
    const renderNumber = useRenderNumber();
    const renderPercentValue = useRenderPercentValue();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tablePage, setTablePage] = useState(1);
    const [tablePageSize, setTablePageSize] = useState(25);
    const [tableSearch, setTableSearch] = useState("");
    const [debouncedTableSearch, setDebouncedTableSearch] = useState("");
    const [tableSort, setTableSort] = useState({key: "profitAfterMarketing", direction: "desc"});
    const tableExpandedStorageKey = `reporting-lab:product-profitability-table-expanded:${project?.id ?? "unknown"}`;
    const [tableExpanded, setTableExpanded] = useState(() => typeof window !== "undefined" && window.sessionStorage.getItem(tableExpandedStorageKey) === "1");

    const sinceString = since.format("YYYY-MM-DD");
    const untilString = until.format("YYYY-MM-DD");
    const centreFilterKey = (centresAllowListed ?? []).join("|");
    const domainFilterKey = (domainsAllowListed ?? []).join("|");

    const productProfitabilityFilter = useMemo(() => ({
        since: sinceString,
        until: untilString,
        selectedCentreIds: centresAllowListed,
        selectedDomainIds: domainsAllowListed,
        limit: tablePageSize,
        page: tablePage,
        search: debouncedTableSearch || null,
        mode: "net",
        sortBy: tableSort.key,
        orderDirection: tableSort.direction,
    }), [sinceString, untilString, centreFilterKey, domainFilterKey, tablePageSize, tablePage, debouncedTableSearch, tableSort]);

    useEffect(() => {
        const timeout = window.setTimeout(() => setDebouncedTableSearch(tableSearch.trim()), 300);
        return () => window.clearTimeout(timeout);
    }, [tableSearch]);

    useEffect(() => {
        window.sessionStorage.setItem(tableExpandedStorageKey, tableExpanded ? "1" : "0");
    }, [tableExpanded, tableExpandedStorageKey]);

    useEffect(() => {
        if (!project?.id) {
            return;
        }

        let active = true;
        setLoading(true);
        setData(null);

        const erpApi = new ErpCalculationApi(getAuthorityConfiguration());

        erpApi.productProfitability({
            projectId: project.id,
            productProfitabilityFilter,
        })
            .then((nextData) => {
                if (!active) {
                    return;
                }

                setData(nextData);
                setLoading(false);
            })
            .catch((error) => {
                if (!active) {
                    return;
                }

                et(error).then((message) => { if (message) toast.error(message); });
            });

        return () => {
            active = false;
        };
    }, [project?.id, productProfitabilityFilter]);

    const products = data?.products ?? [];
    const summary = data?.summary ?? {};
    const hasPurchasePrice = summary.purchasePrice != null;
    const hasProfit = summary.profitAfterMarketing != null;
    const currency = summary.profitAfterMarketing?.currency ?? products.find((product) => product.turnover?.currency)?.turnover?.currency ?? projectCurrency(project);
    const totalTurnover = moneyAmount(summary.turnover);
    const totalPurchase = moneyAmount(summary.purchasePrice);
    const totalMarginBeforeMarketing = moneyAmount(summary.grossMargin);
    const totalAllocatedMarketing = moneyAmount(summary.allocatedMarketingSpend);
    const totalProfitAfterMarketing = moneyAmount(summary.profitAfterMarketing);
    const productsWithoutPurchasePrice = Number(summary.productsWithoutPurchasePrice ?? 0);
    const sourceTotalItems = Number(data?.sourceTotalItems ?? 0);
    const eligibleItems = Number(data?.totalItems ?? products.length);
    const allEligibleItems = Math.max(0, sourceTotalItems - productsWithoutPurchasePrice);
    const purchaseCoverage = dividePercent(allEligibleItems, sourceTotalItems);
    const productPoas = dividePercent(totalMarginBeforeMarketing, totalAllocatedMarketing);

    const topProducts = data?.topProductsByProfit ?? [];
    const topFive = topProducts.filter((product) => moneyAmount(product.profitAfterMarketing) > 0).slice(0, 5);
    const productsSortedByAmount = data?.topProductsByAmount ?? [];
    const quantityChartHeight = Math.max(320, productsSortedByAmount.length * 40 + 64);
    const tablePages = Math.max(1, Number(data?.totalPages ?? 1));
    const normalizedTablePage = Math.min(tablePage, tablePages);
    const visibleProducts = products;
    const toggleSort = (key) => {
        setTableSort((current) => current.key === key
            ? {key, direction: current.direction === "asc" ? "desc" : "asc"}
            : {key, direction: "desc"});
        setTablePage(1);
    };

    useEffect(() => {
        setTablePage(1);
    }, [sinceString, untilString, centreFilterKey, domainFilterKey]);

    const chartTextStyle = {fontSize: 12, color: "#64748b"};
    const chartLegendStyle = {
        textStyle: {fontSize: 14, color: "#64748b"},
        itemWidth: 16,
        itemHeight: 10,
    };

    const profitByProductOption = {
        color: ChannelMixColors,
        textStyle: chartTextStyle,
        grid: {left: 12, right: 18, top: 8, bottom: 12, containLabel: true},
        tooltip: {
            trigger: "axis",
            axisPointer: {type: "shadow"},
            formatter: (params) => formatAxisTooltip(params, (value) => formatChartCurrency(value, currency)),
        },
        xAxis: {
            type: "category",
            data: topProducts.map((product) => product.productName),
            axisLabel: {fontSize: 12, interval: 0, rotate: 68, width: 210, overflow: "truncate", margin: 14, align: "right", formatter: formatProductAxisLabel},
        },
        yAxis: {
            type: "value",
            axisLabel: {fontSize: 12, formatter: (value) => formatChartCurrency(value, currency)},
        },
        series: [
            {
                name: t("reporting_lab.series.profit", {defaultValue: "Marže"}),
                type: "bar",
                data: topProducts.map((product, index) => ({
                    value: moneyToMajor(product.profitAfterMarketing),
                    itemStyle: {color: ChannelMixColors[index % ChannelMixColors.length]},
                })),
                barMaxWidth: 28,
            },
        ],
    };

    const profitByChannelOption = {
        color: ChannelMixColors,
        textStyle: chartTextStyle,
        tooltip: {trigger: "item", formatter: (params) => formatCurrencyItemTooltip(params, currency)},
        legend: {bottom: 8, ...chartLegendStyle},
        series: [
            {
                type: "pie",
                radius: ["38%", "58%"],
                center: ["50%", "32%"],
                label: {show: true, formatter: (params) => formatChartPercent(params.percent), ...chartTextStyle},
                labelLine: {show: true, length: 12, length2: 8},
                data: topFive.map((product) => ({
                    name: product.productName,
                    value: moneyToMajor(product.profitAfterMarketing),
                })),
            },
        ],
    };

    const quantityByProductOption = {
        color: ChannelMixColors,
        textStyle: chartTextStyle,
        tooltip: {
            trigger: "axis",
            axisPointer: {type: "shadow"},
            formatter: (params) => formatAxisTooltip(params, (value) => formatChartNumber(value)),
        },
        grid: {left: 18, right: 24, top: 18, bottom: 20, containLabel: true},
        xAxis: {
            type: "value",
            axisLabel: {fontSize: 12, formatter: (value) => formatChartNumber(value)},
        },
        yAxis: {
            type: "category",
            inverse: true,
            data: productsSortedByAmount.map((product) => product.productName),
            axisLabel: {fontSize: 12, width: 230, overflow: "truncate", margin: 12},
        },
        series: [
            {
                name: t("reporting_lab.products.quantity", {defaultValue: "Kusy"}),
                type: "bar",
                data: productsSortedByAmount.map((product, index) => ({
                    value: Number(product.amount ?? 0),
                    itemStyle: {color: ChannelMixColors[index % ChannelMixColors.length]},
                })),
                barMaxWidth: 26,
                barCategoryGap: "28%",
            },
        ],
    };

    return <WideSecureLayout
        title={t("reporting_lab.pages.product_profitability", {defaultValue: "Produktová ziskovost"})}
        actions={<OrderDashboardFilters hideFilters={false} showMarketingFilters={false} showModeSwitch={false}/>}
    >
        <div className="grid grid-cols-12 gap-4">
            <ReportingPageDescription
                className="col-span-12"
                title={t("reporting_lab.product_profitability.explanation_title", {defaultValue: "K čemu slouží produktová ziskovost"})}
            >
                {t("reporting_lab.product_profitability.explanation", {defaultValue: "Přehled ukazuje ziskovost produktů v dokončených objednávkách. Základ je produktový obrat minus nákupní cena zboží; marketingový spend se na produkty alokuje poměrně podle jejich podílu na obratu. Doprava, platby a ostatní režijní náklady zatím nejsou do produktu alokované."})}
            </ReportingPageDescription>

            {hasProfit && <div className="col-span-12 md:col-span-6 xl:col-span-3">
                <KpiCard loading={loading} label={t("reporting_lab.product_profitability.profit_after_marketing", {defaultValue: "Marže"})} value={renderCurrencyValue({amount: totalProfitAfterMarketing, currency}, true)} meta={t("reporting_lab.kpi.margin", {defaultValue: "Marže"}) + ` ${renderPercentValue(dividePercent(totalProfitAfterMarketing, totalTurnover))}`} tone={totalProfitAfterMarketing < 0 ? "bad" : "default"}/>
            </div>}
            {hasProfit && <div className="col-span-12 md:col-span-6 xl:col-span-3">
                <KpiCard loading={loading} label={t("reporting_lab.product_profitability.margin_before_marketing", {defaultValue: "Marže před marketingem"})} value={renderCurrencyValue({amount: totalMarginBeforeMarketing, currency}, true)} meta={t("reporting_lab.product_profitability.eligible_turnover", {defaultValue: "Obrat započítaných produktů"}) + `: ${renderCurrencyValue({amount: totalTurnover, currency}, true)}`}/>
            </div>}
            {hasProfit && <div className="col-span-12 md:col-span-6 xl:col-span-2">
                <KpiCard loading={loading} label={t("reporting_lab.product_profitability.allocated_marketing", {defaultValue: "Alokovaný marketing"})} value={renderCurrencyValue({amount: totalAllocatedMarketing, currency}, true)} meta={t("reporting_lab.table.purchase", {defaultValue: "Nákup"}) + `: ${renderCurrencyValue({amount: totalPurchase, currency}, true)}`}/>
            </div>}
            {hasPurchasePrice && <div className="col-span-12 md:col-span-6 xl:col-span-2">
                <KpiCard loading={loading} label="POAS" value={productPoas === null ? "-" : `${(productPoas / 100).toLocaleString("cs-CZ", {maximumFractionDigits: 2})}x`} meta={t("reporting_lab.product_profitability.poas_meta", {defaultValue: "Marže zboží / alokovaný marketing"})}/>
            </div>}
            <div className="col-span-12 md:col-span-6 xl:col-span-2">
                <KpiCard loading={loading} label={t("reporting_lab.product_profitability.purchase_coverage", {defaultValue: "Pokrytí nákupní cenou"})} value={renderPercentValue(purchaseCoverage)} meta={t("reporting_lab.product_profitability.purchase_coverage_meta", {defaultValue: "{{eligible}} započítaných / {{missing}} bez ceny", eligible: renderNumber(allEligibleItems), missing: renderNumber(productsWithoutPurchasePrice)})}/>
            </div>

            {hasProfit && <Panel
                loading={loading}
                title={t("reporting_lab.product_profitability.profit_by_product", {defaultValue: "Marže podle produktu"})}
                className="col-span-12 flex flex-col xl:col-span-8"
                contentClassName="flex flex-1 items-center justify-center"
            >
                <EChart option={profitByProductOption} className="h-[28rem]"/>
            </Panel>}
            {hasProfit && <Panel loading={loading} title={t("reporting_lab.product_profitability.top_profit_share", {defaultValue: "Koncentrace marže"})} className="col-span-12 xl:col-span-4">
                <EChart option={profitByChannelOption} className="h-96"/>
            </Panel>}

            <div className="col-span-12 grid grid-cols-12 gap-4">
            <Panel loading={loading} title={t("reporting_lab.product_profitability.amount_by_product", {defaultValue: "Kusy podle produktů ve výběru"})} className={`col-span-12 ${tableExpanded ? "order-2 xl:col-span-12" : "order-1 xl:col-span-5"}`}>
                <EChart option={quantityByProductOption} height={quantityChartHeight}/>
            </Panel>
            <Panel
                loading={loading}
                title={<ExpandablePanelTitle
                    title={t("reporting_lab.product_profitability.product_table", {defaultValue: "Produktový přehled"})}
                    expanded={tableExpanded}
                    onToggle={() => setTableExpanded((expanded) => !expanded)}
                    expandLabel={t("reporting_lab.table.expand", {defaultValue: "Rozšířit"})}
                    collapseLabel={t("reporting_lab.table.collapse", {defaultValue: "Zúžit"})}
                />}
                className={`col-span-12 ${tableExpanded ? "order-1 xl:col-span-12" : "order-2 xl:col-span-7"}`}
            >
                <TableSearch value={tableSearch} onChange={(value) => { setTableSearch(value); setTablePage(1); }} visible={allEligibleItems > 15} t={t}/>
                <div className="overflow-x-auto">
                    <table className="min-w-[80rem] table-fixed divide-y divide-slate-100 text-sm dark:divide-slate-700">
                        <thead>
                        <tr className="text-left text-xs uppercase text-slate-500 dark:text-slate-400">
                            <SortableHeader label={t("reporting_lab.products.product", {defaultValue: "Produkt"})} sortKey="product" sort={tableSort} onSort={toggleSort}/>
                            <SortableHeader label={t("reporting_lab.products.quantity", {defaultValue: "Kusy"})} sortKey="amount" sort={tableSort} onSort={toggleSort} align="right" className="w-20"/>
                            <SortableHeader label={t("reporting_lab.table.revenue", {defaultValue: "Obrat"})} sortKey="turnover" sort={tableSort} onSort={toggleSort} align="right" className="w-32"/>
                            {hasPurchasePrice && <SortableHeader label={t("reporting_lab.table.purchase", {defaultValue: "Nákup"})} sortKey="purchasePrice" sort={tableSort} onSort={toggleSort} align="right" className="w-32"/>}
                            {hasProfit && <SortableHeader label={t("reporting_lab.product_profitability.margin_before_marketing_short", {defaultValue: "Marže zboží"})} sortKey="grossMargin" sort={tableSort} onSort={toggleSort} align="right" className="w-32"/>}
                            <SortableHeader label={t("reporting_lab.product_profitability.allocated_marketing_short", {defaultValue: "Marketing"})} sortKey="allocatedMarketingSpend" sort={tableSort} onSort={toggleSort} align="right" className="w-32"/>
                            {hasProfit && <SortableHeader label={t("reporting_lab.table.contribution", {defaultValue: "Marže"})} tooltip={t("reporting_lab.table.contribution_help", {defaultValue: "Marže = obrat přiřazených objednávek minus nákupní cena zboží a marketingové náklady. Nejde o čistý účetní zisk; nezahrnuje interní náklady dopravy, plateb ani režii."})} sortKey="profitAfterMarketing" sort={tableSort} onSort={toggleSort} align="right" className="w-40 pr-4"/>}
                            {hasProfit && <SortableHeader label={t("reporting_lab.product_profitability.margin_after_marketing", {defaultValue: "Marže %"})} sortKey="marginPercent" sort={tableSort} onSort={toggleSort} align="right" className="w-32"/>}
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                        {visibleProducts.map((product) => {
                            return <tr key={`${product.integrationId ?? "integration"}-${product.productRemoteId ?? product.productName}`}>
                                <td className="max-w-[30rem] py-3 pr-4 font-semibold text-slate-900 dark:text-slate-100">
                                    <div className="truncate">{product.productName}</div>
                                </td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderNumber(product.amount)}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(product.turnover, true)}</td>
                                {hasPurchasePrice && <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(product.purchasePrice, true)}</td>}
                                {hasProfit && <td className={`py-3 pr-4 text-right ${moneyAmount(product.grossMargin) < 0 ? "text-rose-600" : "text-slate-600 dark:text-slate-300"}`}>{renderCurrencyValue(product.grossMargin, true)}</td>}
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(product.allocatedMarketingSpend, true)}</td>
                                {hasProfit && <td className={`py-3 pr-4 text-right ${moneyAmount(product.profitAfterMarketing) < 0 ? "text-rose-600" : "text-slate-600 dark:text-slate-300"}`}>{renderCurrencyValue(product.profitAfterMarketing, true)}</td>}
                                {hasProfit && <td className="py-3 text-right text-slate-600 dark:text-slate-300">{renderPercentValue(dividePercent(moneyAmount(product.profitAfterMarketing), moneyAmount(product.turnover)))}</td>}
                            </tr>;
                        })}
                        </tbody>
                    </table>
                </div>
                <TablePagination page={normalizedTablePage} pageSize={tablePageSize} totalItems={eligibleItems} onPageChange={setTablePage} onPageSizeChange={(value) => { setTablePageSize(value); setTablePage(1); }} t={t}/>
                <div className="mt-4 text-sm text-slate-500 dark:text-slate-400">
                    {t("reporting_lab.product_profitability.scope_summary_complete", {
                        defaultValue: "Zdroj: {{source}} produktů · započítáno do ziskovosti: {{eligible}}",
                        source: renderNumber(sourceTotalItems),
                        eligible: renderNumber(allEligibleItems),
                    })}
                </div>
                {hasPurchasePrice && productsWithoutPurchasePrice > 0 && <div className="mt-3 text-sm text-amber-700 dark:text-amber-300">{t("reporting_lab.product_profitability.missing_purchase_price_complete", {count: productsWithoutPurchasePrice, defaultValue: "{{count}} produktů nemá nákupní cenu a není započítáno do ziskovosti."})}</div>}
                {eligibleItems === 0 && <div className="mt-4 text-sm text-slate-500 dark:text-slate-400">{t("reporting_lab.products.empty", {defaultValue: "Pro vybrané období nejsou dostupná produktová data."})}</div>}
            </Panel>
            </div>
        </div>
    </WideSecureLayout>;
}
